import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icons";
import { useScrollLock } from "./Sheet";

// Zásilkovna's pickup-point map, embedded in our own dialog instead of the
// library's full-page overlay: Packeta.Widget.pick(key, callback, options,
// inElement) renders the map inside a container we size ourselves. A
// centred window on desktop, the whole screen on a phone, with our own
// close button that's always reachable.

let loading = null;
function loadPacketa() {
  if (window.Packeta?.Widget) return Promise.resolve(window.Packeta);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://widget.packeta.com/v6/www/js/library.js";
      s.async = true;
      s.onload = () => (window.Packeta?.Widget ? resolve(window.Packeta) : reject(new Error("The pickup point map didn't load")));
      s.onerror = () => {
        loading = null;
        reject(new Error("The pickup point map didn't load. Check your connection and try again."));
      };
      document.head.appendChild(s);
    });
  }
  return loading;
}

const WIDGET_LANGS = ["cs", "sk", "en", "de", "pl", "hu", "ro", "uk"];
function widgetLanguage() {
  const l = String(navigator.language || "en").slice(0, 2).toLowerCase();
  return WIDGET_LANGS.includes(l) ? l : "en";
}

// Packeta's own points have an id; partner carriers' (InPost, Hermes …)
// come as carrierId + carrierPickupPointId instead.
export function pointFromWidget(point) {
  const partner = point.carrierId && point.carrierPickupPointId;
  return {
    id: partner ? null : String(point.id),
    carrierId: partner ? String(point.carrierId) : null,
    carrierPickupPointId: partner ? String(point.carrierPickupPointId) : null,
    name: point.place || point.name,
    address: [point.street, [point.zip, point.city].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    country: String(point.country || "").toUpperCase(),
    locker: /z-?box|box|locker|automat/i.test(`${point.name} ${point.place} ${point.pickupPointType || ""}`),
  };
}

export default function PacketaPicker({ apiKey, country, vendors, onPick, onClose }) {
  const host = useRef(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const done = useRef(false);
  useScrollLock(true);

  useEffect(() => {
    let cancelled = false;
    loadPacketa()
      .then((Packeta) => {
        if (cancelled || !host.current) return;
        const options = { country: country.toLowerCase(), language: widgetLanguage(), appIdentity: "the-creaks-eshop" };
        if (vendors?.length) options.vendors = vendors;
        Packeta.Widget.pick(
          apiKey,
          (point) => {
            if (done.current) return;
            done.current = true;
            if (point) onPick(pointFromWidget(point));
            else onClose();
          },
          options,
          host.current
        );
        setState("ready");
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message);
          setState("error");
        }
      });
    return () => {
      cancelled = true;
      try {
        window.Packeta?.Widget?.close();
      } catch {
        // already gone
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, country]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="sh-pk-root" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sh-pk" role="dialog" aria-modal="true" aria-label="Choose a pickup point">
        <header className="sh-pk-head">
          <div>
            <h2>Choose a pickup point</h2>
            <p>Zásilkovna points and Z-BOX lockers near you</p>
          </div>
          <button className="sh-iconbtn" onClick={onClose} aria-label="Close map" autoFocus>
            <Icon name="close" size={22} />
          </button>
        </header>
        <div className="sh-pk-body">
          <div className="sh-pk-host" ref={host} />
          {state === "loading" && (
            <div className="sh-pk-state">
              <span className="sh-spinner" /> Loading the map…
            </div>
          )}
          {state === "error" && (
            <div className="sh-pk-state">
              <p>{error}</p>
              <button className="sh-btn sh-btn-ghost" onClick={onClose}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
